import React, {ChangeEvent, useState} from 'react';
import {toSnakeCase, toTitleCase} from "../../../../services/string";
import {TextField, ToggleField} from "../Field";
import {plural, singular} from "pluralize";
import {FormikContextType, useFormikContext} from "formik";
import {OperatorGlossaryEntryData} from "../../../../types/Operator";
import Button from "../../../Button";
import {RefreshIcon} from "@heroicons/react/solid";

// Types ----

type GlossaryEntryFieldSetProps = {
  term: string
  name?: string
}

// Helpers ----

function getEntry(form: FormikContextType<any>, path: string): OperatorGlossaryEntryData|undefined {
  return path
    .split('.')
    .reduce((object, key) => object[key], form.values as Record<string, any>) as OperatorGlossaryEntryData|undefined
}

// Component ----

function GlossaryEntryFieldSet({name, term}: GlossaryEntryFieldSetProps) {
  const form = useFormikContext();

  const termSingular = toTitleCase(singular(term));
  const termPlural = toTitleCase(plural(term));
  const termPath = name ?? toSnakeCase(term);

  const entry = getEntry(form, termPath);

  const [replacement, setReplacement] = useState<string>(entry?.replacement ?? '');
  const [replacementPlural, setReplacementPlural] = useState<string>(entry?.replacement_plural ?? '');

  const isDefaultTerm = termSingular === toTitleCase(replacement) && termPlural === toTitleCase(replacementPlural);

  function changeReplacement(newReplacement: string): void {
    if(!replacementPlural || plural(replacement) === replacementPlural) {
      changeReplacementPlural(plural(newReplacement))
    }

    setReplacement(newReplacement)
    void form.setFieldValue(`${termPath}.replacement`, newReplacement)
  }

  function changeReplacementPlural(newReplacementPlural: string): void {
    setReplacementPlural(newReplacementPlural)
    void form.setFieldValue(`${termPath}.replacement_plural`, newReplacementPlural)
  }

  function changePreserveCase(toggled: boolean): void {
    void form.setFieldValue(`${termPath}.preserve_case`, toggled)
  }

  function resetEntry(): void {
    setReplacement(termSingular);
    form.setFieldValue(`${termPath}.replacement`, termSingular)
      .then(() => {
        form.validateField(`${termPath}.replacement`);
      });
    setReplacementPlural(termPlural);
    form.setFieldValue(`${termPath}.replacement_plural`, termPlural)
      .then(() => {
        form.validateField(`${termPath}.replacement_plural`);
      });
  }

  return (
    <fieldset className="mt-4">
      <div className="mb-2">
        <legend className="font-medium text-sm">{termSingular}</legend>
      </div>

      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 bg-gray-50 rounded-md p-2">
        <div>
          <TextField
            name={`${termPath}.replacement`}
            value={replacement}
            onChange={(event: ChangeEvent<HTMLInputElement>) => changeReplacement(event.currentTarget.value)}
            label={'Singular'}
            max={250}
            placeholder={termSingular}
            autoComplete="off"
          />
        </div>

        <div>
          <TextField
            name={`${termPath}.replacement_plural`}
            value={replacementPlural}
            onChange={(event: ChangeEvent<HTMLInputElement>) => changeReplacementPlural(event.currentTarget.value)}
            label={'Plural'}
            max={250}
            placeholder={termPlural}
            autoComplete="off"
          />
        </div>

        <div className="sm:col-span-2 flex justify-between">
          <ToggleField
            name={`${termPath}.preserve_case`}
            onChange={changePreserveCase}
            label={'Preserve Case'}
            disabled={!entry?.replacement}
            short
            inline
          />

          <Button
            size={Button.sizes.TINY}
            color={Button.colors.WHITE}
            icon={RefreshIcon}
            onClick={resetEntry}
            disabled={isDefaultTerm}
          >
            Reset
          </Button>
        </div>
      </div>
    </fieldset>
  );
}

// Export ----

export default GlossaryEntryFieldSet;